// =============================================================================
// Visibility & Access Constants
// Centralized constants for the ClickUp-like visibility model
// =============================================================================

import { Crown, Pencil, MessageSquare, Eye, type LucideIcon } from 'lucide-react';
import type { PermissionLevel } from '@/modules/project/data/permissionRepository';

export type Visibility = 'public' | 'private';

/**
 * Default permission level granted to members on public content.
 * ClickUp-like: members can at least edit public items.
 * Change to 'view' for a more restrictive default.
 */
export const DEFAULT_PUBLIC_MEMBER_PERMISSION: PermissionLevel = 'edit';

/**
 * Permission level labels (French, ClickUp-like)
 */
export const PERMISSION_LEVEL_LABELS: Record<PermissionLevel, string> = {
  full: 'Accès complet',
  edit: 'Peut modifier',
  comment: 'Peut commenter',
  view: 'Lecture seule',
};

/**
 * Centralized permission config with icons and colors (ClickUp-like)
 * Used across all permission dialogs for DRY consistency
 */
export const PERMISSION_CONFIG: Record<PermissionLevel, { label: string; icon: LucideIcon; color: string }> = {
  full: { label: 'Accès complet', icon: Crown, color: 'text-amber-500' },
  edit: { label: 'Peut modifier', icon: Pencil, color: 'text-blue-500' },
  comment: { label: 'Peut commenter', icon: MessageSquare, color: 'text-green-500' },
  view: { label: 'Lecture seule', icon: Eye, color: 'text-slate-500' },
};

/**
 * Ordered list of permission levels for iteration
 */
export const PERMISSION_LEVELS: PermissionLevel[] = ['full', 'edit', 'comment', 'view'];

/**
 * Visibility labels (French)
 */
export const VISIBILITY_LABELS: Record<Visibility, { label: string; description: string }> = {
  public: {
    label: 'Public',
    description: 'Visible par tous les collaborateurs du workspace',
  },
  private: {
    label: 'Privé',
    description: 'Accessible uniquement sur invitation explicite',
  },
};

/**
 * Permission source labels (French)
 */
export const PERMISSION_SOURCE_LABELS = {
  project: 'Accès direct',
  space: 'Hérité',
  org: 'Admin org',
  public: 'Public',
} as const;
